import React, { Fragment, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Card, Col, Form, FormGroup, Row } from "react-bootstrap";
import moment from "moment/moment";
import ImageUrl from "../../ImageUrl";
import { memberLoans, memberOutstanding, memberProfile } from "../../services/member";

// Which tab each sidebar menu path opens. The menu is role driven, so a member
// may have any subset of these.
const TAB_FOR_PATH = {
  "/member/dashboard": "profile",
  "/member/loans": "loans",
  "/member/outstanding": "outstanding",
};

// Self-service portal for a logged in member (role 4). Read-only: every API
// here resolves the member from their token, so there is no member id to pass.
const MemberDashboard = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(
    TAB_FOR_PATH[location.pathname] || "profile"
  );

  // The three menu links render this same page, so follow the route.
  useEffect(() => {
    setActiveTab(TAB_FOR_PATH[location.pathname] || "profile");
  }, [location.pathname]);
  const [details, setDetails] = useState(null);
  const [path, setPath] = useState("/images/member/");
  const [loans, setLoans] = useState([]);
  const [individualLoans, setIndividualLoans] = useState([]);
  const [outstanding, setOutstanding] = useState([]);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([memberProfile(), memberLoans(), memberOutstanding()])
      .then(([prof, loan, out]) => {
        if (prof?.status === true) {
          setDetails(prof.data?.details || null);
          setPath(prof.data?.path || "/images/member/");
        }
        if (loan?.status === true) {
          setLoans(loan.data?.loan_details || []);
          setIndividualLoans(loan.data?.individual_loan_details || []);
        }
        if (out?.status === true) {
          setOutstanding(out.data?.loans || []);
          setTotalOutstanding(out.data?.total_outstanding || 0);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  function errorphoto(ev) {
    ev.target.src = require("../../assets/images/women.png");
    ev.target.onerror = null;
  }

  // One tab per loan, the same way the staff member profile lists them.
  const allLoans = [
    ...loans.map((l) => ({ ...l, type: "Group" })),
    ...individualLoans.map((l) => ({ ...l, type: "Individual" })),
  ];
  const activeLoans = allLoans.filter((l) => String(l.loan_status) !== "3").length;
  const closedLoans = allLoans.filter((l) => String(l.loan_status) === "3").length;

  const loanTabId = (index) => `loan-${index}`;
  const isPaid = (entry) =>
    entry.loanpaid != null && entry.loanpaid !== "0" && entry.loanpaid !== "";
  const loanStatusLabel = (status) =>
    String(status) === "2"
      ? "Rejected"
      : String(status) === "3"
      ? "Completed"
      : "Ongoing";
  const pendingDues = outstanding.reduce(
    (sum, l) => sum + (Number(l.pending_dues) || 0),
    0
  );

  // The "My Loans" menu link lands on "loans", which is not a tab of its own:
  // open the first loan once they have loaded. With no loans it stays put and
  // shows the empty message.
  useEffect(() => {
    if (activeTab === "loans" && allLoans.length > 0) {
      setActiveTab(loanTabId(0));
    }
  }, [activeTab, allLoans.length]);

  // Matches the "Label : value" rows used on the staff member profile.
  const detailRow = (label, value) => (
    <FormGroup className="form-group" key={label}>
      <Row className="row-sm">
        <Col md={5} sm={5}>
          <h3 className="studentdetail-a">
            {label} <span className="pull-end">:</span>
          </h3>
        </Col>
        <Col md={7} sm={7}>
          <h3 className="student-detail-b">{value || "-"}</h3>
        </Col>
      </Row>
    </FormGroup>
  );

  const statCard = (variant, icon, title, value) => (
    <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
      <div className={`card custom-card ${variant}`}>
        <div className="card-body">
          <div className="card-item">
            <div className="card-item-icon card-icon">
              <img alt="icon" src={icon} />
            </div>
            <div className="card-item-title mb-2">
              <label className="main-content-label tx-13 font-weight-bold mb-1">
                {title}
              </label>
            </div>
            <div className="card-item-body">
              <div className="card-item-stat">
                <h6 className="font-weight-bold">{value}</h6>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Fragment>
        <div className="main-container container-fluid">
          <div className="inner-body">
            <div className="page-header">
              <h2 className="main-content-title tx-24 mg-b-5">My Dashboard</h2>
            </div>
            <p>Loading your details...</p>
          </div>
        </div>
      </Fragment>
    );
  }

  if (!details) {
    return (
      <Fragment>
        <div className="main-container container-fluid">
          <div className="inner-body">
            <div className="page-header">
              <h2 className="main-content-title tx-24 mg-b-5">My Dashboard</h2>
            </div>
            <div className="alert alert-warning">
              We could not load your member record. Please contact your branch.
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <div className="main-container container-fluid">
        <div className="inner-body">
          {/* Page Header */}
          <div className="page-header">
            <h2 className="main-content-title tx-24 mg-b-5">
              Welcome, {details.member_name}
            </h2>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a>Home</a>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                My Dashboard
              </li>
            </ol>
          </div>
          {/* End Page Header */}

          {/* Summary tiles */}
          <div className="row row-sm">
            {statCard(
              "custom-card-a",
              require("../../assets/images/icons/app loan.png"),
              "My Active Loans",
              activeLoans
            )}
            {statCard(
              "custom-card-b",
              require("../../assets/images/icons/principal balance.png"),
              "Total Outstanding",
              totalOutstanding
            )}
            {statCard(
              "custom-card-c",
              require("../../assets/images/icons/today collection.png"),
              "Pending Dues",
              pendingDues
            )}
            {statCard(
              "custom-card-g",
              require("../../assets/images/icons/closed loan white.png"),
              "Closed Loans",
              closedLoans
            )}
          </div>

          <div className="row row-sm">
            {/* Left Profile Card */}
            <div className="col-xl-3 col-lg-12 col-md-12">
              <div className="card custom-card">
                <div className="card-body text-center item-user">
                  <div className="profile-pic">
                    <div className="profile-pic-img">
                      <img
                        alt="avatar"
                        src={details.photo ? ImageUrl + path + details.photo : ""}
                        onError={(e) => errorphoto(e)}
                      />
                    </div>
                    <h5 className="mt-3 mb-0 font-weight-semibold">
                      {details.member_name}
                    </h5>
                  </div>
                </div>
                <ul className="item1-links nav nav-tabs mb-0">
                  <li className="nav-item">
                    <a className="float-end nav-links">{details.phone_no || "-"}</a>
                    <a className="nav-links nav-link">
                      <i className="fa fa-phone-square"></i> Phone No
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="float-end nav-links">{details.group_name || "-"}</a>
                    <a className="nav-links nav-link">
                      <i className="fa fa-users"></i> Group
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="float-end nav-links">{details.branch_name || "-"}</a>
                    <a className="nav-links nav-link">
                      <i className="fa fa-building"></i> Branch
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="float-end nav-links">{details.group_center || "-"}</a>
                    <a className="nav-links nav-link">
                      <i className="fa fa-map-marker"></i> Center
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="float-end nav-links">{details.kyc_status || "-"}</a>
                    <a className="nav-links nav-link">
                      <i className="fa fa-id-card"></i> KYC Status
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right Tabs Section */}
            <div className="col-xl-9 col-lg-12 col-md-12">
              <div className="card custom-card mg-b-20" id="tabs-style2">
                <div className="card-body">
                  <div className="tabs-container">
                    {/* Tab Headers */}
                    <div className="tabs-menu">
                      <button
                        className={`tab-button ${activeTab === "profile" ? "active" : ""}`}
                        onClick={() => setActiveTab("profile")}
                      >
                        <i className="mdi mdi-account-box-outline"></i> My Profile
                      </button>
                      {allLoans.map((loan, index) => (
                        <button
                          key={loanTabId(index)}
                          className={`tab-button ${
                            activeTab === loanTabId(index) ? "active" : ""
                          }`}
                          onClick={() => setActiveTab(loanTabId(index))}
                        >
                          <i className="fa fa-money headericons"></i> Loan {index + 1} -{" "}
                          {loan.scheme_name}
                        </button>
                      ))}
                      <button
                        className={`tab-button ${activeTab === "outstanding" ? "active" : ""}`}
                        onClick={() => setActiveTab("outstanding")}
                      >
                        <i className="mdi mdi-file-document-outline"></i> Outstanding / NOC
                      </button>
                    </div>

                    {/* Tab Content */}
                    <div className="tab-content">
                      {activeTab === "profile" && (
                        <div className="row">
                          <div className="card custom-card mg-t-15 col-xl-12 col-lg-12 col-md-12">
                            <Card className="custom-card body-bg">
                              <Card.Header>
                                <div className="d-md-flex ms-auto">
                                  <label className="main-content-label my-auto pt-2">
                                    My Detail
                                  </label>
                                </div>
                              </Card.Header>
                              <div className="row">
                                <div className="col-xl-6 col-lg-6 col-md-12">
                                  <Card.Body>
                                    <Form className="form-horizontal">
                                      {detailRow("Name", details.member_name)}
                                      {detailRow(
                                        "DOB",
                                        details.member_dob
                                          ? (moment(details.member_dob, ["DD/MM/YYYY", "DD-MM-YYYY", "DD.MM.YYYY", "YYYY-MM-DD", moment.ISO_8601], true).isValid()
                                              ? moment(details.member_dob, ["DD/MM/YYYY", "DD-MM-YYYY", "DD.MM.YYYY", "YYYY-MM-DD", moment.ISO_8601], true).format("DD-MM-YYYY")
                                              : details.member_dob)
                                          : "-"
                                      )}
                                      {detailRow("Age", details.member_age)}
                                      {detailRow("Gender", details.gender)}
                                      {detailRow("Marital Status", details.marital_status)}
                                      {detailRow("Husband Name", details.husband_name)}
                                      {detailRow("Phone Number", details.phone_no)}
                                      {detailRow("Alternate Number", details.alternate_no)}
                                    </Form>
                                  </Card.Body>
                                </div>
                                <div className="col-xl-6 col-lg-6 col-md-12">
                                  <Card.Body>
                                    <Form className="form-horizontal">
                                      {detailRow("Native Place", details.native_place)}
                                      {detailRow("Occupation", details.occupation)}
                                      {detailRow("Permanent Address", details.permanent_address)}
                                      {detailRow("Temporary Address", details.temporary_address)}
                                      {detailRow("Bank", details.bank_name)}
                                      {detailRow("Group", details.group_name)}
                                      {detailRow("Center", details.group_center)}
                                      {detailRow("Branch", details.branch_name)}
                                    </Form>
                                  </Card.Body>
                                </div>
                              </div>
                              <Card.Footer>
                                <p className="text-muted mb-0">
                                  To correct any of these details, please contact your
                                  branch.
                                </p>
                              </Card.Footer>
                            </Card>
                          </div>
                        </div>
                      )}

                      {activeTab === "loans" && allLoans.length === 0 && (
                        <p className="text-muted mg-t-15">You have no loans yet.</p>
                      )}

                      {allLoans.map((loan, index) => {
                        if (activeTab !== loanTabId(index)) {
                          return null;
                        }

                        return (
                          <div key={loanTabId(index)} className="loan-details-content">
                            <div className="exam-result-container table-wrapper">
                              <div className="d-md-flex ms-auto mb-2">
                                <label className="my-auto pt-2 exam-title">
                                  <strong>
                                    {loan.group_name || `${loan.type} Loan`}
                                  </strong>
                                </label>
                              </div>
                              <div className="result-summary">
                                <p>
                                  <strong>Scheme:</strong> {loan.scheme_name}
                                </p>
                                <p>
                                  <strong>Loan Date:</strong> {loan.loan_date}
                                </p>
                                <p>
                                  <strong>Loan Amount:</strong> ₹ {loan.loan_amount}
                                </p>
                                <p>
                                  <strong>Interest ₹ Rs:</strong> {loan.interest}
                                </p>
                                <p>
                                  <strong>Due:</strong> {loan.no_of_dues}
                                </p>
                                <p>
                                  <strong>Total:</strong> ₹ {loan.total_amount}
                                </p>
                                <p>
                                  <strong>Status:</strong>{" "}
                                  <span
                                    className={
                                      String(loan.loan_status) === "2"
                                        ? "result-fail"
                                        : "result-pass"
                                    }
                                  >
                                    {loanStatusLabel(loan.loan_status)}
                                  </span>
                                </p>
                              </div>

                              {/* Loan Entries Table */}
                              <table className="result-table">
                                <thead>
                                  <tr>
                                    <th>Due No</th>
                                    <th>Due Date</th>
                                    <th>Paid Date</th>
                                    <th>Principle</th>
                                    <th>Interest</th>
                                    <th>Total</th>
                                    <th>Paid</th>
                                    <th>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(loan.entries || []).map((entry, entryIndex) => (
                                    <tr key={entryIndex}>
                                      <td>{entryIndex + 1}</td>
                                      <td>{entry.loan_duedate}</td>
                                      <td>{isPaid(entry) ? entry.loan_paiddate : ""}</td>
                                      <td>{entry.loan_principle}</td>
                                      <td>{entry.loan_interest}</td>
                                      <td>{entry.loan_total}</td>
                                      <td>{entry.loanpaid}</td>
                                      <td>
                                        <span
                                          className={
                                            isPaid(entry) ? "result-pass" : "result-fail"
                                          }
                                        >
                                          {isPaid(entry) ? "Paid" : "Pending"}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              <hr />
                            </div>
                          </div>
                        );
                      })}

                      {activeTab === "outstanding" && (
                        <div className="row">
                          <div className="col-12 mg-t-15">
                            {outstanding.length === 0 ? (
                              <p className="text-muted">Nothing outstanding.</p>
                            ) : (
                              <Fragment>
                                <div className="table-container">
                                  <table className="responsive-table">
                                    <thead>
                                      <tr>
                                        <th>S.No</th>
                                        <th>Type</th>
                                        <th>Loan Date</th>
                                        <th>Total Due</th>
                                        <th>Paid</th>
                                        <th>Outstanding</th>
                                        <th>Pending Dues</th>
                                        <th>NOC</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {outstanding.map((loan, i) => (
                                        <tr key={i}>
                                          <td>{i + 1}</td>
                                          <td>{loan.type}</td>
                                          <td>{loan.loan_date}</td>
                                          <td>{loan.total_due}</td>
                                          <td>{loan.total_paid}</td>
                                          <td className="font-weight-bold">
                                            {loan.outstanding}
                                          </td>
                                          <td>{loan.pending_dues}</td>
                                          <td>
                                            {loan.settled ? (
                                              <span className="badge bg-success">
                                                Eligible
                                              </span>
                                            ) : (
                                              <span className="text-muted">
                                                Not settled
                                              </span>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                <p className="text-muted mt-3 mb-0">
                                  An NOC can be collected from your branch once a loan
                                  is fully settled.
                                </p>
                              </Fragment>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default MemberDashboard;
